// The running totals of a desk sheet, by tender (PMS-TERMINAL-LINK-BRIEF
// §8.8o, operator 2026-09-26: « a running total for each kind of payment is
// important (esp. cash) »).
//
// Since 2026-09-27 (operator: « the totals are those collected on the page we
// see »), a band counts ITS PAGE's money of the day: on Départs what the
// departing guests paid that day — the check-out shift — and on Arrivées what
// the arriving guests paid — the check-in shift. The day's register is the
// two together. Vega's lines and the form's not yet in Vega (⚠), each once.
// Refunds come off the tile of the tender they went back on. What sits in the
// drawer comes first — it has to be counted and banked — then the electronic
// tenders, card split CB / AMEX / VAD because each reconciles against its own
// statement. A movement that is not money in a known tender (a deposit refund
// with no mode, a settlement gap) is named underneath, never totalled.
//
// Picking a tile lights the payments it counts on the sheet below. The sheet
// re-reads every minute, so the figures run through the shift.

import type { ReactNode } from 'react';
import type { TakingsBucket } from '@lemontaigu/pms-core';
import { fmtAmount } from './logic.js';
import { takingsSummary, type TakingsLine, type TakingsTile } from './departureSheet.js';
import { NotInVega } from './PaymentList.js';

export type LitBucket = Exclude<TakingsBucket, 'outside'> | null;

export interface DayTakingsProps {
  /** « Encaissé aux départs du 25/09 ». */
  title: ReactNode;
  lines: TakingsLine[];
  lit: LitBucket;
  onLight: (bucket: LitBucket) => void;
}

const plural = (n: number) => (n > 1 ? 's' : '');

function Tile({ t, lit, onLight }: { t: TakingsTile; lit: LitBucket; onLight: (b: LitBucket) => void }) {
  const on = lit === t.bucket;
  return (
    <button
      type="button"
      className={`pms-tile${t.bucket === 'cash' ? ' is-cash' : ''}${t.movements === 0 ? ' is-empty' : ''}`}
      aria-pressed={on}
      aria-label={`${t.label} ${fmtAmount(t.netCents)} €`}
      onClick={() => onLight(on ? null : t.bucket)}
    >
      <span className="pms-tile-label">{t.label}</span>
      <span className="pms-tile-amt">{t.movements === 0 ? '—' : fmtAmount(t.netCents)}</span>
      <span className="pms-tile-sub">
        {t.movements === 0 ? 'rien' : `${t.movements} opération${plural(t.movements)}`}
        {t.outCents !== 0 && ` · dont remb. ${fmtAmount(t.outCents)}`}
        {t.notInVega > 0 && (
          <>
            {' · '}
            <NotInVega /> {fmtAmount(t.notInVegaCents)}
          </>
        )}
      </span>
    </button>
  );
}

export function DayTakings({ title, lines, lit, onLight }: DayTakingsProps) {
  const s = takingsSummary(lines);
  const drawer = s.tiles.filter((t) => t.drawer);
  const electronic = s.tiles.filter((t) => !t.drawer);
  return (
    <section className="pms-takings" aria-label="Encaissements de la page">
      <div className="pms-section-bar">
        <h3 className="pms-section-title">
          {title} <span className="pms-sub">· Vega et formulaire</span>
        </h3>
        {s.notInVega > 0 && (
          <span className="pms-stale">
            <NotInVega /> {s.notInVega} pas encore dans Vega
          </span>
        )}
      </div>
      <div className="pms-takings-tiles">
        <div className="pms-takings-group" role="group" aria-label="Au tiroir">
          <span className="pms-takings-caption">Au tiroir</span>
          <div className="pms-takings-row">
            {drawer.map((t) => (
              <Tile key={t.bucket} t={t} lit={lit} onLight={onLight} />
            ))}
          </div>
        </div>
        <div className="pms-takings-group" role="group" aria-label="Électronique">
          <span className="pms-takings-caption">Électronique</span>
          <div className="pms-takings-row">
            {electronic.map((t) => (
              <Tile key={t.bucket} t={t} lit={lit} onLight={onLight} />
            ))}
          </div>
        </div>
        <div className="pms-takings-group">
          <span className="pms-takings-caption">Total</span>
          <div className="pms-takings-row">
            <div className="pms-tile is-total" aria-label={`Total ${fmtAmount(s.totalCents)} €`}>
              <span className="pms-tile-label">Total</span>
              <span className="pms-tile-amt">{fmtAmount(s.totalCents)}</span>
              <span className="pms-tile-sub">
                {s.movements} opération{plural(s.movements)}
              </span>
            </div>
          </div>
        </div>
      </div>
      {s.outside.length > 0 && (
        <p className="pms-hint" title="Mouvements Vega qui ne sont pas de l'argent reçu dans un mode connu : non comptés dans le total">
          Hors caisse, non compté :{' '}
          {s.outside.map((o) => `${o.label} ${fmtAmount(o.cents)}`).join(' · ')}
        </p>
      )}
    </section>
  );
}
